import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";

import { fetchBookingHistory } from "../../../../../Redux/slice/features/bookingSlice";
import { showCancellationPage } from "../../../../../Redux/slice/features/showCancel";

const CancelButton = React.memo(({ history, userID, onCancel }) => {
  const today = new Date();
  const checkInDate = new Date(history.checkInDate);
  const diffDays = Math.ceil((checkInDate - today) / (1000 * 60 * 60 * 24));
  const isCancelled = history.intCancelStatus === 1;

  const isDisabled =
    isCancelled ||
    (userID !== 1 && history.bookingType === "Online Hotel Booking(Failed)") ||
    diffDays <= 2;

  return (
    <span
      title={
        isCancelled
          ? "Already cancelled"
          : isDisabled
          ? "Payment failed, already cancelled, or check-in is too soon"
          : ""
      }
    >
      <button
        className="btn btn-danger"
        onClick={() => onCancel(history.BookingID)}
        disabled={isDisabled}
        style={{
          maxHeight: "50px",
          fontSize: isCancelled ? "10px" : "14px",
        }}
      >
        <i
          className="bi bi-x-circle-fill me-2"
          style={{ fontSize: isCancelled ? "10px" : "14px" }}
        ></i>
        {isCancelled ? "Cancelled" : isDisabled ? "cancel" : "Cancel"}
      </button>
    </span>
  );
});

const BookingTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);

  const { data, status, error } = useSelector((state) => state.booking);
  const userID = user?.user.customerId;

  const isAdmin = user?.user?.UserName === "admin";
  // console.log("User info:", user?.user);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    bookingRefNo: "",
  });
  const [filterError, setFilterError] = useState("");

  useEffect(() => {
    if (userID) {
      if (isAdmin) {
        dispatch(fetchBookingHistory({ pageSize: 100 }));
      } else {
        // Normal user(manager): fetch only their bookings
        dispatch(fetchBookingHistory({ UserId: userID, pageSize: 100 }));
      }
    }
  }, [userID, dispatch]);

  const handleShow = useCallback(() => {
    if (
      filters.fromDate &&
      filters.toDate &&
      new Date(filters.fromDate) > new Date(filters.toDate)
    ) {
      setFilterError("From Date cannot be after To Date.");
      return;
    }
    setFilterError("");
    if (isAdmin) {
      // Admin: no UserId filter
      dispatch(fetchBookingHistory({ ...filters, pageSize: 100 }));
    } else {
      // Normal user: always send UserId
      dispatch(
        fetchBookingHistory({ ...filters, UserId: userID, pageSize: 100 })
      );
    }
  }, [dispatch, userID, filters, isAdmin]);

  const handleCancellation = useCallback(
    (bookingId) => {
      dispatch(showCancellationPage());
      navigate(`/cancellation/${bookingId}`);
    },
    [dispatch, navigate]
  );

  const handleViewBooking = useCallback((bookingId) => {
    window.open(`/booking-view/${bookingId}`, "_blank", "width=800,height=600");
  }, []);

  // This was causing a duplicate declaration

  const transformedData = useMemo(() => {
    if (status !== "succeeded" || !Array.isArray(data)) return [];

    return data.map((history) => ({
      ...history,
      BookingRefNo: history.BookingRefNo,
      BookingDate: history.BookingDate,
      hotelName: history.PropertyName,
      customerName: history.CustomerName,
      checkInDate: history.CheckinDate,
      checkOutDate: history.CheckOutDate,
      bookingType: history.Remarks,
      bookingAmount: history.TotalAmount,
      BookingID: history.BookingID,
      intCancelStatus: history.intCancelStatus,
      CreatedBy: history.CreatedBy,
    }));
  }, [data, status]);

  const handleExcelExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, "booking_history.xlsx");
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "Booking Ref No",
        selector: (row) => row.BookingRefNo,
        sortable: true,
        minWidth: "180px",
      },
      {
        name: "Booking Date",
        selector: (row) => row.BookingDate,
        sortable: true,
        minWidth: "150px",
      },
      {
        name: "Hotel Name",
        selector: (row) => row.hotelName,
        sortable: true,
        minWidth: "180px",
      },
      {
        name: "Customer Name",
        selector: (row) => row.customerName,
        sortable: true,
        minWidth: "200px",
      },
      {
        name: "Check-in",
        selector: (row) => row.checkInDate,
        sortable: true,
        minWidth: "120px",
      },
      {
        name: "Check-out",
        selector: (row) => row.checkOutDate,
        sortable: true,
        minWidth: "140px",
      },
      {
        name: "Booking Type",
        selector: (row) => row.bookingType,
        sortable: true,
        minWidth: "200px",
      },
      {
        name: "Amount",
        selector: (row) => `₹${row.bookingAmount?.toFixed(2) || "0.00"}`,
        sortable: true,
        minWidth: "140px",
      },
      {
        name: "View",
        cell: (row) => (
          <button
            type="button"
            className="btn btn-sm btn-info"
            onClick={() => handleViewBooking(row.BookingID)}
          >
            <i className="bi bi-eye"></i>
          </button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      ...(!isAdmin
        ? []
        : [
            {
              name: "Cancel",
              cell: (row) => (
                <div
                  style={{
                    minWidth: 150,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CancelButton
                    history={row}
                    userID={userID}
                    onCancel={handleCancellation}
                  />
                </div>
              ),
              ignoreRowClick: true,
              allowOverflow: true,
              button: true,
            },
          ]),
    ];

    return baseColumns;
  }, [handleViewBooking, handleCancellation, userID, isAdmin]);

  const customStyles = {
    table: {
      style: {
        minWidth: "1300px !important",
      },
    },
    rows: {
      style: {
        minHeight: "45px",
      },
    },
    headCells: {
      style: {
        fontWeight: "bold",
        fontSize: "14px",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        position: "sticky",
        top: 0,
        zIndex: 1,
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: "clip",
      },
    },
    headRow: {
      style: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        backgroundColor: "#f8f9fa",
      },
    },
    cells: {
      style: {
        border: "1px solid #dee2e6",
        padding: "8px",
      },
    },
  };

  if (status === "loading") {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (status === "failed" && error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          {error || "Failed to fetch booking history"}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">From Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, fromDate: e.target.value }))
                }
                max={filters.toDate || undefined}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">To Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, toDate: e.target.value }))
                }
                min={filters.fromDate || undefined}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Booking Ref No</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Booking Reference"
                value={filters.bookingRefNo}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, bookingRefNo: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 d-flex align-items-end gap-2">
              <button className="btn btn-primary w-100" onClick={handleShow}>
                <i className="bi bi-search me-2"></i>Show
              </button>
              <CSVLink
                data={transformedData}
                filename="booking_history.csv"
                className="btn btn-sm btn-outline-primary"
                title="Export CSV"
              >
                <i className="bi bi-file-earmark"></i>
              </CSVLink>
              <button
                className="btn btn-sm btn-outline-success"
                onClick={handleExcelExport}
                title="Export Excel"
              >
                <i className="bi bi-file-earmark-excel"></i>
              </button>
            </div>
          </div>
          {filterError && (
            <div className="row mt-2">
              <div className="col text-danger">{filterError}</div>
            </div>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={transformedData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[50, 100, 500]}
        highlightOnHover
        striped
        persistTableHead
        customStyles={customStyles}
        noDataComponent="No booking history found."
        responsive
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 250px)"
      />
    </div>
  );
};

export default React.memo(BookingTable);
